import React from 'react';

import { Column } from '../../../../utils/models';
import { PDRTItem } from '../pdrtReportItems';
import { calculateWallLossPercentage, calculateWallLossValue, concatClassNames, formatTime } from '../../../../utils/utilities';
import { APP_DATE_FORMAT } from '../../../../utils/constants';
import { getReadingStyle, getResultItemStyle, getUTMColorCodes } from '../../../../utils/data';

import Conditional from '../../../common/conditional';
import PAUTConstraint from '../../custom-paut/paut-constraint';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import classNames from './style.module.scss';

const getSinglePDRTSummary = (item: PDRTItem) => {
	return {
		srNo: item.id,
		location: item.location,
		zoneNumber: item.zoneNumber,
		scanNumber: item.scanNumber,
		nominalThickness: item.nominalThickness,
		corrosionAllowance: item.corrosionAllowance,
		thickness: item.thickness,
		result: item.result,
		remark: item.remark,
		wallLoss: isNaN(item.nominalThickness as number) ? 'N/A' : calculateWallLossValue(item.nominalThickness as number, item.thickness).toFixed(2),
		wallLossPercentage: calculateWallLossPercentage(item.nominalThickness as number, item.thickness),
	};
}

const PDRTDataPage: React.FC<PDRTItem> = (props) => {
	return (
		<div className={classNames.drtDataPage}>
			<div className="container-fluid overflow-hidden">
				<div className="row">
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>Source</div>
							<div className={classNames.drtDataPageItemRight}>{props.source}</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Serial Number
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sourceSerialNumber}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								RT Technique
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.rtTechnique}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Source Size (mm)
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sourceSize}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Source Activity (Volts)
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sourceActivity}
							</div>
						</div>
					</div>
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>DRT Scanner Name</div>
							<div className={classNames.drtDataPageItemRight}>{props.drtScannerName}</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>DRT Scanner Serial Number</div>
							<div className={classNames.drtDataPageItemRight}>{props.drtScannerSerialNumber}</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>Image Type</div>
							<div className={classNames.drtDataPageItemRight}>{props.imageFilmType}</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>Pipe Diameter (inch)</div>
							<div className={classNames.drtDataPageItemRight}>{props.pipeDia}</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>Contrast Used</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.contrastUsed}
							</div>
						</div>
					</div>
				</div>
			</div>
			
			<Conditional isVisible={false}>
				<div className={classNames.drtDataPageSeparator}></div>
			</Conditional>

			<div className="container-fluid overflow-hidden">
				<div className="row">
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Comparator Ball Size (OD mm)
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.comparatorBallSize}
							</div>
						</div>
					</div>
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Source Conformity Inspection Date
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sourceConformityInspectionDate ? formatTime(props.sourceConformityInspectionDate, APP_DATE_FORMAT) : 'N/A'}
							</div>
						</div>
					</div>
				</div>
			</div>
			
			
			<div className="container-fluid overflow-hidden">
				<div className="row">
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Comparator Location
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.comparatorLocation}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>SDD</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sdd}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>ODD</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.odd}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Source Manufacturer
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.sourceManufacturer}
							</div>
						</div>
					</div>
					<div className="col p-0">
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Exposure Time (Minutes)
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.exposureTime}
							</div>
						</div>
						<div className={classNames.drtDataPageItem}>
							<div className={classNames.drtDataPageItemLeft}>
								Material Specification
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.materialSpecification}
							</div>
						</div>
						<div className={concatClassNames([classNames.drtDataPageItem, 'col p-0'])}>
							<div className={classNames.drtDataPageItemLeft}>
								Date of Inspection
							</div>
							<div className={classNames.drtDataPageItemRight}>
								{props.inspectionDate ? formatTime(props.inspectionDate, APP_DATE_FORMAT) : 'N/A'}
							</div>
						</div>
						<div className={concatClassNames([classNames.drtDataPageItem, 'col p-0'])}>
						<div className={classNames.drtDataPageItemLeft}>
							DRT Scanner Calibration Due Date
						</div>
						<div className={classNames.drtDataPageItemRight}>
							{props.drtScannerCalibrationDate ? formatTime(props.drtScannerCalibrationDate, APP_DATE_FORMAT) : 'N/A'}
						</div>
					</div>
					</div>
				</div>
			</div>

			<Conditional isVisible={false}>
				<div className={classNames.drtDataPageSeparator}></div>
			</Conditional>

			<div className="container-fluid overflow-hidden p-0">
				<div className={classNames.drtDataPageSynonyms}>
					SDD = SFD = Source to detector distance; ODD = OFD = Object to
					detector distance TDW = Tangential Double Wall (TDW) Technique
				</div>
			</div>

			<div className={classNames.drtDataPageTitle}>Result</div>
			
			<div className="d-flex justify-content-start align-items-center">
				<div className={concatClassNames([classNames.drtDataPageSN, 'flex-fill'])}>
					<div className={classNames.drtDataPageSNLeft}>Zone Number</div>
					<div className={concatClassNames([classNames.drtDataPageSNRight, classNames.drtDataPageSNRightBordered])}>{props.zoneNumber}</div>
				</div>
				<div className={concatClassNames([classNames.drtDataPageSN, 'flex-fill'])}>
					<div className={classNames.drtDataPageSNLeft}>Scan Number</div>
					<div className={classNames.drtDataPageSNRight}>{props.scanNumber}</div>
				</div>
			</div>

			{props.image && props.image.length > 0 && (
				<div className={classNames.drtDataPageImage}>
					<img src={props.image[0].src} alt={props.image[0].caption} />
				</div>
			)}

			<Conditional isVisible={!props.image || props.image.length === 0}>
				<PAUTConstraint />
			</Conditional>

			<div className={classNames.drtDataPageBottom}>
				<PDFTablePage
					columns={columns}
					data={[getSinglePDRTSummary(props)]}
				/>

				<div className={classNames.drtDataPageLimitation}>
					LIMITATION (IF ANY): {props.limitation}
				</div>
			</div>
		</div>
	);
};

const columns: Column[] = [
	{
		path: 'srNo',
		label: 'Sr. No.',
	},
	{
		path: 'location',
		label: 'Location',
	},
	{
		path: 'nominalThickness',
		label: 'Nominal Thickness (mm)',
	},
	{
		path: 'corrosionAllowance',
		label: 'Corrosion Allowance (mm)',
	},
	{
		path: 'thickness',
		label: 'Measured Thickness (mm)',
	},
	{
		path: 'wallLoss',
		label: 'Wall Loss (mm)',
		style: (item: PDRTItem) => getReadingStyle(+item.nominalThickness, +item.corrosionAllowance as number, item.thickness, getUTMColorCodes()) as React.CSSProperties
	},
	{
		path: 'wallLossPercentage',
		label: 'Wall Loss (%)',
		style: (item: PDRTItem) => getReadingStyle(+item.nominalThickness, +item.corrosionAllowance as number, item.thickness, getUTMColorCodes()) as React.CSSProperties
	},
	{
		path: 'result',
		label: 'Result',
		style: (item: any) => getResultItemStyle(item),
	},
	{
		path: 'remark',
		label: 'Remark',
	},
];

export { getSinglePDRTSummary };
export default PDRTDataPage;
